import { useCallback, useState } from "react";

/**
 * Generic validation-error shape.
 *
 * - `fields`  — set of top-level field keys that failed validation.
 * - `items`   — keyed by item id → set of sub-field keys that failed
 *               (e.g. handling-unit rows, grid rows, etc.).
 */
export type FormValidationErrors = {
	fields: Set<string>;
	items: Record<number, Set<string>>;
};

const emptyErrors: FormValidationErrors = {
	fields: new Set(),
	items: {},
};

function hasAnyError(errors: FormValidationErrors): boolean {
	return errors.fields.size > 0 || Object.keys(errors.items).length > 0;
}

/**
 * Reusable form-validation hook.
 *
 * Supply a `validationFn` that inspects your form state and returns
 * a `FormValidationErrors` describing every failing field. The hook
 * manages the error state and exposes helpers to clear individual
 * field / item-field errors (for "clear on change" behaviour).
 *
 * @example
 * ```ts
 * const { validationErrors, validate, clearFieldError } =
 *   useFormValidation((state: MyFormState) => {
 *     const errors: FormValidationErrors = { fields: new Set(), items: {} };
 *     if (!state.name) errors.fields.add("name");
 *     return errors;
 *   });
 * ```
 */
export const useFormValidation = <TArgs extends unknown[]>(
	validationFn: (...args: TArgs) => FormValidationErrors,
) => {
	const [validationErrors, setValidationErrors] =
		useState<FormValidationErrors>(emptyErrors);

	/** Run validation. Returns `true` when the form is valid. */
	const validate = useCallback(
		(...args: TArgs): boolean => {
			const errors = validationFn(...args);
			const isValid = !hasAnyError(errors);

			if (isValid) {
				setValidationErrors(emptyErrors);
			} else {
				setValidationErrors(errors);
			}

			return isValid;
		},
		[validationFn],
	);

	/** Clear a single top-level field error. */
	const clearFieldError = useCallback((field: string) => {
		setValidationErrors((prev) => {
			if (!prev.fields.has(field)) return prev;
			const next = new Set(prev.fields);
			next.delete(field);
			return { ...prev, fields: next };
		});
	}, []);

	/** Clear a single sub-field error on an item row. */
	const clearItemFieldError = useCallback(
		(itemId: number, fieldKey: string) => {
			setValidationErrors((prev) => {
				const existing = prev.items[itemId];
				if (!existing?.has(fieldKey)) return prev;
				const next = new Set(existing);
				next.delete(fieldKey);
				const items = { ...prev.items };
				if (next.size === 0) {
					delete items[itemId];
				} else {
					items[itemId] = next;
				}
				return { ...prev, items };
			});
		},
		[],
	);

	/** Reset all validation errors. */
	const resetValidation = useCallback(() => {
		setValidationErrors(emptyErrors);
	}, []);

	/** Whether a top-level field currently has an error. */
	const hasFieldError = useCallback(
		(field: string) => validationErrors.fields.has(field),
		[validationErrors],
	);

	return {
		validationErrors,
		validate,
		clearFieldError,
		clearItemFieldError,
		resetValidation,
		hasFieldError,
	};
};
